import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11 } from "styled-components";
import React from "react";
import { takeFirst } from "~/features/app/utils";
import { Headline } from "~/features/ui/headline";
import { Media } from "~/features/ui/media";
import { Link, type Props as LinkProps } from "~/features/ui/link";
import { Icon } from "~/features/ui/icon";
import { VideoControls } from "~/features/ui/video/video-controls";
import { getThemeStyle } from "~/features/ui/get-theme-style";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { Wrap } from "~/features/ui/wrap";
import { Button } from "~/features/ui/button";
import { useNavLayout } from "~/features/app/app-header-v2/context";
import { RichText } from "~/features/ui/rich-text";
import ArrowLeft from "~/public/icons/arrow-left.svg";
const fragment = graphql(`
  fragment EditorialHero on hero_editorialHero_BlockType {
    __typename
    headline: baseHeadline
    text: baseRichText
    media: baseMedia {
      ...Media
    }
  }
`);
type Props = FragmentType<typeof fragment> & {
  backLink?: LinkProps;
};
export function EditorialHero(props: Props) {
  const {
    backLink,
    ...rest
  } = props;
  const {
    headline,
    text,
    media: _media
  } = useFragment(fragment, rest);
  const themeStyle = getThemeStyle({
    mode: "light"
  });
  const {
    isAnnouncementBarVisible
  } = useNavLayout();
  const topSpacing = isAnnouncementBarVisible ? {
    "marginTop": "calc(var(--announcement-bar-height) + var(--header-height))"
  } : {
    "marginTop": "var(--header-height)"
  };
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const media = takeFirst(_media);
  const isVideo = media && ("videoAsset" in media || "src" in media);
  return <_StyledDiv $_css={[{
    "borderTopWidth": "0px !important"
  }, themeStyle.background, themeStyle.border]}>
      <_StyledDiv2>
        <_StyledDiv3 />
        <_StyledDiv4>
          <Media {...media} imageProps={{
          fit: "cover",
          rounded: false,
          loading: "eager",
          priority: true
        }} inlineVideoProps={{
          fit: "cover",
          videoRef,
          rounded: false,
          priority: true
        }} externalVideoProps={{
          fit: "cover",
          rounded: false
        }} />
        </_StyledDiv4>

        {backLink && <_StyledDiv5 $_css2={[{
        "position": "absolute",
        "left": "0",
        "top": "0",
        "zIndex": "10",
        "padding": "1.6rem",
        "--tw-text-opacity": "1",
        "color": "rgb(255 255 255 / var(--tw-text-opacity))",
        "@media (min-width: 1024px)": {
          "padding": "4rem"
        }
      }, topSpacing]}>
            <Button as={Link} href={`${process.env.NEXT_PUBLIC_URL}/${backLink.href}`} variant="underline">
              <Icon icon={ArrowLeft} size="16" />
              {backLink?.text}
            </Button>
          </_StyledDiv5>}

        {isVideo && <_StyledDiv6 $_css3={[{
        "position": "absolute",
        "right": "0",
        "zIndex": "10",
        "--tw-text-opacity": "1",
        "color": "rgb(255 255 255 / var(--tw-text-opacity))"
      }, isAnnouncementBarVisible ? {
        "top": "calc(var(--announcement-bar-height) + var(--header-height))"
      } : {
        "top": "var(--header-height)"
      }]}>
            <VideoControls videoRef={videoRef} variant="tertiary" size="medium" />
          </_StyledDiv6>}

        <_StyledDiv7>
          <_StyledDiv8>
            <Wrap mode="light">
              <_StyledDiv9>
                <_StyledDiv10>
                  {headline && <_StyledHeadline text={headline} />}
                  {text && <RichText text={text} />}
                </_StyledDiv10>
              </_StyledDiv9>
            </Wrap>
            <div />
          </_StyledDiv8>
        </_StyledDiv7>
      </_StyledDiv2>
    </_StyledDiv>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-6lmda1-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-6lmda1-1"
})({
  "position": "relative",
  "isolation": "isolate",
  "zIndex": "10",
  "display": "flex",
  "height": "100svh",
  "alignItems": "flex-end"
});
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-6lmda1-2"
})({
  "pointerEvents": "none",
  "position": "absolute",
  "inset": "0",
  "zIndex": "10",
  "backgroundImage": "linear-gradient(to bottom, var(--tw-gradient-stops))",
  "--tw-gradient-from": "rgb(0 7 4 / 0.2) var(--tw-gradient-from-position)",
  "--tw-gradient-to": "rgb(0 7 4 / 0) var(--tw-gradient-to-position)",
  "--tw-gradient-stops": "var(--tw-gradient-from), var(--tw-gradient-to)"
});
var _StyledDiv4 = _styled4("div").withConfig({
  componentId: "sc-sf-6lmda1-3"
})({
  "position": "absolute",
  "inset": "0"
});
var _StyledDiv5 = _styled5("div").withConfig({
  componentId: "sc-sf-6lmda1-4"
})`${p => p.$_css2}`;
var _StyledDiv6 = _styled6("div").withConfig({
  componentId: "sc-sf-6lmda1-5"
})`${p => p.$_css3}`;
var _StyledDiv7 = _styled7("div").withConfig({
  componentId: "sc-sf-6lmda1-6"
})({
  "position": "relative",
  "zIndex": "10",
  "display": "flex",
  "width": "100%",
  "flexDirection": "column",
  "alignItems": "flex-end"
});
var _StyledDiv8 = _styled8("div").withConfig({
  componentId: "sc-sf-6lmda1-7"
})({
  "position": "relative",
  "width": "100%",
  "borderTopWidth": "1px",
  "borderTopColor": "rgb(255 255 255 / 0.3)"
});
var _StyledDiv9 = _styled9("div").withConfig({
  componentId: "sc-sf-6lmda1-8"
})({
  "backgroundColor": "rgb(0 7 4 / 0.3)",
  "paddingLeft": "4rem",
  "paddingRight": "4rem",
  "paddingTop": "1.6rem",
  "paddingBottom": "1.6rem",
  "--tw-text-opacity": "1",
  "color": "rgb(255 255 255 / var(--tw-text-opacity))",
  "--tw-backdrop-blur": "blur(8px)",
  "backdropFilter": "var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia)",
  "@media (min-width: 1024px)": {
    "marginLeft": "4rem",
    "marginRight": "4rem",
    "borderLeftWidth": "1px",
    "borderRightWidth": "1px",
    "borderLeftColor": "rgb(255 255 255 / 0.3)",
    "borderRightColor": "rgb(255 255 255 / 0.3)",
    "padding": "4rem"
  }
});
var _StyledDiv10 = _styled10("div").withConfig({
  componentId: "sc-sf-6lmda1-9"
})({
  "display": "flex",
  "flexDirection": "column",
  "gap": "0.8rem",
  "--tw-text-opacity": "1",
  "color": "rgb(255 255 255 / var(--tw-text-opacity))",
  "@media (min-width: 1024px)": {
    "gap": "1.6rem"
  }
});
var _StyledHeadline = /*#__PURE__*/_styled11(Headline).withConfig({
  displayName: "editorial-hero___StyledHeadline",
  componentId: "sc-wdpj7w-0"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "4.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.044rem",
    "fontWeight": "400"
  }
});